import { useState, useRef } from "react";
import { Upload, X, FileText, Image as ImageIcon, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { uploadFile, resolveMedia } from "@/lib/api";
import { toast } from "sonner";

export default function MediaUpload({ value, onChange, accept = "image/*,application/pdf", label = "Archivo" }) {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(value || "");
  const inputRef = useRef(null);

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await uploadFile(file);
      const url = res.data.url;
      setPreview(url);
      onChange(url);
      toast.success("Archivo subido correctamente");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al subir archivo");
    }
    setUploading(false);
    if (inputRef.current) inputRef.current.value = "";
  };

  const handleClear = () => {
    setPreview("");
    onChange("");
  };

  const isImage = preview && (preview.match(/\.(jpg|jpeg|png|webp|gif)(\?|$)/i) || preview.includes("image"));
  const isPdf = preview && (preview.match(/\.pdf(\?|$)/i) || preview.includes("pdf"));
  const previewSrc = resolveMedia(preview);

  return (
    <div data-testid="media-upload" className="space-y-2">
      <label className="text-sm font-medium text-zinc-700">{label}</label>
      {preview ? (
        <div className="relative border border-zinc-200 p-3 bg-zinc-50">
          <button
            type="button"
            onClick={handleClear}
            className="absolute top-2 right-2 p-1 bg-zinc-900/70 text-white rounded-full hover:bg-zinc-900"
          >
            <X size={12} />
          </button>
          {isImage ? (
            <img src={previewSrc} alt="Preview" className="w-full h-32 object-contain bg-white" />
          ) : isPdf ? (
            <div className="flex items-center gap-3 py-3">
              <FileText size={24} className="text-red-500" />
              <a href={previewSrc} target="_blank" rel="noopener noreferrer" className="text-sm text-emerald-700 truncate hover:underline">
                {preview.split("/").pop()}
              </a>
            </div>
          ) : (
            <div className="flex items-center gap-3 py-3">
              <ImageIcon size={24} className="text-zinc-400" />
              <span className="text-sm text-zinc-600 truncate">{preview}</span>
            </div>
          )}
        </div>
      ) : (
        <div
          onClick={() => !uploading && inputRef.current?.click()}
          className="border-2 border-dashed border-zinc-300 p-6 text-center cursor-pointer hover:border-emerald-400 transition-colors"
        >
          {uploading ? (
            <div className="flex flex-col items-center gap-2">
              <Loader2 size={24} className="text-emerald-600 animate-spin" />
              <span className="text-sm text-zinc-500">Subiendo...</span>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2">
              <Upload size={24} className="text-zinc-400" />
              <span className="text-sm text-zinc-500">Haga clic para subir</span>
              <span className="text-xs text-zinc-400">Imagenes (JPG, PNG, WebP) o PDFs. Max 10MB</span>
            </div>
          )}
        </div>
      )}
      <input ref={inputRef} type="file" accept={accept} onChange={handleFileChange} className="hidden" />
    </div>
  );
}
